import { api } from './api.js';
import { icon } from './icons.js';
import { escapeHtml as e, idOf } from './utils.js';
import { editorHtml, editorText, installRichEditor, insertHtml, restoreSelection, selectionOffsets, setEditorHtml } from './rich-composer.js';

const drafts = new Map();
let draftEpoch = 0;
const keyFor = (scope, id) => `${scope}:${id}`;
let activePopover = null;

export function writingControls({ external = false, macroOpen = false } = {}) {
  const action = (name) => external ? `data-shared-action="${name}"` : `data-action="${name}"`;
  const format = (name) => external ? `data-shared-format="${name}"` : `data-format="${name}"`;
  return `<button type="button" class="icon-button" ${format('bold')} aria-label="Grassetto" title="Grassetto"><b>B</b></button><button type="button" class="icon-button" ${format('italic')} aria-label="Corsivo" title="Corsivo"><i>I</i></button><button type="button" class="icon-button" ${format('underline')} aria-label="Sottolineato" title="Sottolineato"><u>U</u></button><button type="button" class="icon-button" ${action('insert-link')} aria-label="Link" title="Link">${icon('link')}</button><button type="button" class="icon-button" ${action('insert-image')} aria-label="Immagine" title="Immagine">${icon('image')}</button><button type="button" class="icon-button" ${action('attachment')} aria-label="Allega file" title="Allega file">${icon('attachment')}</button><button type="button" class="icon-button" ${action('insert-video')} aria-label="Video" title="Video">${icon('video')}</button><button type="button" class="button button-subtle macro-button" ${action('macros')} aria-expanded="${macroOpen}">${icon('macro')} Macro</button>`;
}

export function renderSharedComposer({ scope, id, html = '', text = '', attachmentIds = [], attachments = [], placeholder = 'Scrivi un messaggio…' }) {
  const key = keyFor(scope, id);
  const current = drafts.get(key) || { html: html || e(text).replace(/\n/g, '<br>'), text, attachmentIds: attachmentIds.map(idOf), attachments };
  drafts.set(key, current);
  if (current.uploading) queueMicrotask(() => repaintUpload(key));
  const visibleHtml = String(current.html).replace(/cid:boi-([a-f0-9]{24})/gi, (_match, assetId) => `/api/v1/composer-assets/${assetId}?inline=1`);
  return `<div class="shared-email-composer" data-shared-composer data-scope="${e(scope)}" data-scope-id="${e(id)}"><div class="rich-editor shared-email-editor" data-shared-editor contenteditable="true" role="textbox" aria-multiline="true" data-placeholder="${e(placeholder)}">${visibleHtml}</div><div class="shared-attachments">${(current.attachments || []).map(file => `<span>📎 ${e(file.filename)} <button type="button" data-shared-remove="${e(file.id)}" aria-label="Rimuovi ${e(file.filename)}">×</button></span>`).join('')}</div><div class="reply-format-tools shared-email-tools">${writingControls({ external: true })}<input type="file" data-shared-files multiple hidden><input type="file" data-shared-images accept="image/png,image/jpeg,image/gif,image/webp" multiple hidden></div><div class="shared-macro-menu" data-shared-macros hidden><label>Cerca macro<input type="search" data-shared-search placeholder="Cerca per nome, tag o testo…"></label><div class="shared-macro-layout"><div data-shared-list></div><div data-shared-preview>Passa su una macro per vedere l’anteprima.</div></div></div><p data-shared-upload-status role="status" aria-live="polite">${e(current.uploadStatus || '')}</p><p class="form-error" data-shared-error role="alert">${e(current.uploadError || '')}</p></div>`;
}

export function sharedComposerValue(host) {
  if (!host) return { html: '', text: '', attachmentIds: [] };
  const editor = host.querySelector('[data-shared-editor]');
  const key = keyFor(host.dataset.scope, host.dataset.scopeId);
  const previous = drafts.get(key) || {};
  const value = { ...previous, html: editorHtml(editor), text: editorText(editor), attachmentIds: previous.attachmentIds || [] };
  drafts.set(key, value);
  return value;
}

export function clearSharedComposer(scope, id) { drafts.delete(keyFor(scope, id)); }
export function resetSharedComposers() { draftEpoch++; drafts.clear(); activePopover?.remove(); activePopover = null; }
export function sharedComposerUploading(host) { return Boolean(host && drafts.get(keyFor(host.dataset.scope, host.dataset.scopeId))?.uploading); }

function activeHost(key) {
  return [...document.querySelectorAll('[data-shared-composer]')].find(host => keyFor(host.dataset.scope, host.dataset.scopeId) === key);
}

function repaintUpload(key) {
  const host = activeHost(key), current = drafts.get(key);
  if (!host || !current) return;
  host.querySelector('[data-shared-upload-status]').textContent = current.uploadStatus || '';
  host.querySelector('[data-shared-upload-status]').setAttribute('aria-busy', String(Boolean(current.uploading)));
  host.querySelector('[data-shared-error]').textContent = current.uploadError || '';
  host.querySelectorAll('[data-shared-files],[data-shared-images],[data-shared-action="attachment"],[data-shared-action="insert-image"]').forEach(input => { input.disabled = Boolean(current.uploading); });
  host.querySelector('.shared-attachments').innerHTML = current.attachments.map(file => `<span>📎 ${e(file.filename)} <button type="button" data-shared-remove="${e(file.id)}" aria-label="Rimuovi ${e(file.filename)}">×</button></span>`).join('');
  host.closest('form')?.querySelectorAll('button[type="submit"]').forEach(button => {
    if (current.uploading) { if (!button.hasAttribute('data-upload-disabled')) button.dataset.uploadDisabled = String(button.disabled); button.disabled = true; }
    else if (button.hasAttribute('data-upload-disabled')) { button.disabled = button.dataset.uploadDisabled === 'true'; delete button.dataset.uploadDisabled; }
  });
}

function emitChange(host) {
  sharedComposerValue(host);
  host.dispatchEvent(new Event('input', { bubbles: true }));
}

function closePopover() {
  if (!activePopover) return;
  activePopover.controller.abort();
  activePopover.element.remove();
  activePopover = null;
}

function linkPopover(button, kind, host) {
  closePopover();
  const editor = host.querySelector('[data-shared-editor]');
  const selection = selectionOffsets(editor) || drafts.get(keyFor(host.dataset.scope, host.dataset.scopeId))?.selection;
  const selectedText = window.getSelection()?.toString() || '';
  const element = document.createElement('form');
  element.className = 'composer-link-popover';
  element.setAttribute('role', 'dialog');
  element.setAttribute('aria-label', kind === 'video' ? 'Inserisci video' : 'Inserisci link');
  element.innerHTML = `<label>URL<input name="url" type="url" required placeholder="https://example.com"></label><label>Testo visibile<input name="label" type="text" value="${e(selectedText)}"></label><p class="form-error" role="alert"></p><div><button type="button" class="button" data-close>Annulla</button><button type="submit" class="button button-primary">Inserisci</button></div>`;
  document.body.append(element);
  const rect = button.getBoundingClientRect();
  element.style.left = `${Math.max(8, Math.min(rect.left, window.innerWidth - element.offsetWidth - 8))}px`;
  element.style.top = `${Math.max(8, rect.top - element.offsetHeight - 8)}px`;
  const controller = new AbortController();
  activePopover = { element, controller };
  element.querySelector('[name=url]').focus();
  element.addEventListener('click', event => { if (event.target.closest('[data-close]')) { closePopover(); button.focus(); } }, { signal: controller.signal });
  element.addEventListener('submit', event => {
    event.preventDefault();
    try {
      const url = new URL(element.elements.url.value);
      if (!['https:', 'http:', 'mailto:'].includes(url.protocol) || kind === 'video' && url.protocol !== 'https:') throw new Error();
      restoreSelection(editor, selection);
      insertHtml(editor, `<a href="${e(url.href)}" target="_blank" rel="noopener noreferrer">${e(element.elements.label.value.trim() || (kind === 'video' ? 'Guarda il video' : url.href))}</a>`);
      closePopover();
    } catch { element.querySelector('.form-error').textContent = 'Inserisci un URL valido.'; }
  }, { signal: controller.signal });
  queueMicrotask(() => {
    if (!activePopover || activePopover.element !== element) return;
    document.addEventListener('pointerdown', event => { if (!element.contains(event.target) && !button.contains(event.target)) closePopover(); }, { signal: controller.signal });
    document.addEventListener('keydown', event => { if (event.key === 'Escape') { event.preventDefault(); closePopover(); button.focus(); } }, { signal: controller.signal });
  });
}

function macrosHtml(macros, query = '') {
  const needle = query.toLowerCase().trim();
  return macros.filter(macro => `${macro.name} ${(macro.tags || []).join(' ')} ${macro.bodyText || ''}`.toLowerCase().includes(needle))
    .map(macro => `<button type="button" data-shared-macro="${idOf(macro)}" data-preview="${e(macro.bodyText || '')}"><b>${e(macro.name)}</b><small>${e((macro.tags || []).join(' · '))}</small></button>`).join('') || '<p>Nessuna macro corrispondente.</p>';
}

async function applyMacro(host, macroId, orderId = null) {
  const editor = host.querySelector('[data-shared-editor]');
  const key = keyFor(host.dataset.scope, host.dataset.scopeId);
  const error = host.querySelector('[data-shared-error]');
  try {
    const context = host.dataset.scope === 'ORDER' ? { orderContextId: host.dataset.scopeId } : host.dataset.scope === 'CASE' ? { shippingCaseId: host.dataset.scopeId } : { shippingRequestId: host.dataset.scopeId, orderId: host.closest('form')?.querySelector('[name="orderId"]')?.value || undefined };
    const result = (await api(`/macros/${macroId}/resolve`, { method: 'POST', body: { ...context, ...(orderId ? { orderId } : {}) } })).data;
    restoreSelection(editor, drafts.get(key)?.selection);
    insertHtml(editor, result.html || e(result.body).replace(/\n/g, '<br>'));
    host.querySelector('[data-shared-macros]').hidden = true;
    error.textContent = result.missing?.length ? 'Sostituisci o rimuovi le variabili non disponibili prima di inviare.' : '';
  } catch (failure) {
    if (failure.code === 'MACRO_ORDER_REQUIRED' && failure.meta?.orders?.length) {
      error.innerHTML = `<label>Scegli un ordine<select data-shared-order-choice>${failure.meta.orders.map(order => `<option value="${e(order.id)}">${e(order.name)}</option>`).join('')}</select></label><button type="button" class="button" data-shared-macro-order="${e(macroId)}">Applica macro</button>`;
    } else error.textContent = failure.message;
  }
}

export function installSharedEmailComposer() {
  document.addEventListener('focusin', event => {
    const editor = event.target.closest?.('[data-shared-editor]');
    if (!editor) return;
    installRichEditor(editor);
    if (editor.querySelector('img[src^="cid:boi-"]')) setEditorHtml(editor, editor.innerHTML);
  });
  document.addEventListener('input', event => {
    const host = event.target.closest?.('[data-shared-composer]');
    if (!host) return;
    if (event.target.matches('[data-shared-search]')) {
      const key = keyFor(host.dataset.scope, host.dataset.scopeId);
      host.querySelector('[data-shared-list]').innerHTML = macrosHtml(drafts.get(key)?.macros || [], event.target.value);
      return;
    }
    if (event.target.matches('[data-shared-editor]')) sharedComposerValue(host);
  });
  document.addEventListener('selectionchange', () => {
    const editor = document.activeElement?.closest?.('[data-shared-editor]');
    if (!editor) return;
    const host = editor.closest('[data-shared-composer]'), key = keyFor(host.dataset.scope, host.dataset.scopeId);
    const draft = drafts.get(key) || {};
    draft.selection = selectionOffsets(editor);
    drafts.set(key, draft);
  });
  document.addEventListener('pointerover', event => {
    const button = event.target.closest?.('[data-shared-macro]');
    if (button) button.closest('[data-shared-composer]').querySelector('[data-shared-preview]').textContent = button.dataset.preview || '';
  });
  document.addEventListener('focusin', event => {
    const button = event.target.closest?.('[data-shared-macro]');
    if (button) button.closest('[data-shared-composer]').querySelector('[data-shared-preview]').textContent = button.dataset.preview || '';
  });
  document.addEventListener('click', async event => {
    const button = event.target.closest?.('[data-shared-format],[data-shared-action],[data-shared-macro],[data-shared-macro-order],[data-shared-remove]');
    if (!button) return;
    const host = button.closest('[data-shared-composer]'), editor = host.querySelector('[data-shared-editor]');
    const key = keyFor(host.dataset.scope, host.dataset.scopeId);
    installRichEditor(editor);
    if (button.dataset.sharedFormat) {
      event.preventDefault();
      restoreSelection(editor, drafts.get(key)?.selection);
      document.execCommand(button.dataset.sharedFormat, false);
      emitChange(host);
      return;
    }
    if (button.dataset.sharedRemove) {
      const current = drafts.get(key);
      current.attachmentIds = current.attachmentIds.filter(id => id !== button.dataset.sharedRemove);
      current.attachments = current.attachments.filter(file => file.id !== button.dataset.sharedRemove);
      editor.querySelectorAll('img').forEach(img => { if (img.getAttribute('src')?.includes(button.dataset.sharedRemove)) img.remove(); });
      button.parentElement.remove();
      emitChange(host);
      return;
    }
    if (button.dataset.sharedMacro) {
      event.preventDefault();
      await applyMacro(host, button.dataset.sharedMacro);
      return;
    }
    if (button.dataset.sharedMacroOrder) {
      event.preventDefault();
      await applyMacro(host, button.dataset.sharedMacroOrder, host.querySelector('[data-shared-order-choice]')?.value);
      return;
    }
    switch (button.dataset.sharedAction) {
      case 'insert-link': case 'insert-video': linkPopover(button, button.dataset.sharedAction === 'insert-video' ? 'video' : 'link', host); break;
      case 'insert-image': host.querySelector('[data-shared-images]').click(); break;
      case 'attachment': host.querySelector('[data-shared-files]').click(); break;
      case 'macros': {
        const menu = host.querySelector('[data-shared-macros]');
        menu.hidden = !menu.hidden;
        button.setAttribute('aria-expanded', String(!menu.hidden));
        if (!menu.hidden) {
          try {
            const macros = (await api('/macros')).data;
            if (!host.isConnected || !drafts.has(key)) return;
            drafts.get(key).macros = macros;
            host.querySelector('[data-shared-list]').innerHTML = macrosHtml(macros);
            host.querySelector('[data-shared-search]').focus();
          } catch (failure) { host.querySelector('[data-shared-error]').textContent = failure.message; }
        }
      } break;
    }
  });
  document.addEventListener('change', async event => {
    const input = event.target.closest?.('[data-shared-files],[data-shared-images]');
    if (!input?.files?.length) return;
    const host = input.closest('[data-shared-composer]'), editor = host.querySelector('[data-shared-editor]');
    const key = keyFor(host.dataset.scope, host.dataset.scopeId);
    const selected = drafts.get(key)?.selection;
    const current = sharedComposerValue(host), selection = [...input.files], startedEpoch = draftEpoch;
    if (current.uploading) return;
    input.value = '';
    if (current.attachmentIds.length + selection.length > 20 || [...current.attachments, ...selection].reduce((sum, file) => sum + Number(file.size || 0), 0) > 20 * 1024 * 1024) {
      current.uploadError = 'Limite allegati del messaggio: 20 file e 20 MB complessivi.';
      repaintUpload(key); return;
    }
    current.uploading = true; current.uploadError = '';
    const failures = [];
    try {
      for (const file of selection) {
        if (startedEpoch !== draftEpoch || !drafts.has(key)) return;
        drafts.get(key).uploadStatus = `Caricamento: ${file.name}`; repaintUpload(key);
        const data = new FormData(); data.append('scope', host.dataset.scope); data.append('scopeId', host.dataset.scopeId); data.append('files', file);
        try {
          const files = (await api('/composer-assets', { method: 'POST', body: data })).data;
          const stored = drafts.get(key);
          if (startedEpoch !== draftEpoch || !stored) return;
          stored.attachments.push(...files); stored.attachmentIds.push(...files.map(file => file.id));
          const mounted = activeHost(key);
          if (input.matches('[data-shared-images]')) {
            const imageHtml = files.map(file => `<img src="/api/v1/composer-assets/${file.id}?inline=1" alt="${e(file.filename)}">`).join('');
            if (mounted) { const target = mounted.querySelector('[data-shared-editor]'); restoreSelection(target, selected); insertHtml(target, imageHtml); }
            else stored.html += imageHtml;
          }
          if (mounted) emitChange(mounted);
          repaintUpload(key);
        } catch (failure) { failures.push(`${file.name}: ${failure.message}`); }
      }
    } finally {
      const stored = drafts.get(key);
      if (startedEpoch !== draftEpoch || !stored) return;
      stored.uploading = false; stored.uploadStatus = failures.length ? 'Gli allegati caricati correttamente sono conservati.' : 'Caricamento completato.';
      stored.uploadError = failures.length ? `${failures.join(' · ')}. Seleziona nuovamente i file non caricati per riprovare.` : '';
      repaintUpload(key);
    }
  });
}
